// Canvas-space geometry. Pointer coordinates are converted before reaching here,
// so snapping and group offsets remain consistent at every editor zoom level.
export const CANVAS_GRID = 8

// Touching edges are valid. Compare actual rectangles, not a group's bounding
// box: a stationary widget may legitimately sit in the space between members.
export function overlappingWidgetIds(moving, stationary, layouts = {}) {
  const ids = new Set()
  const epsilon = 0.000001
  for (const widget of moving) {
    const a = layouts[widget.id] || widget.layout
    for (const other of stationary) {
      if (widget.id === other.id) continue
      const b = other.layout
      if (a.x < b.x + b.w - epsilon && a.x + a.w > b.x + epsilon
          && a.y < b.y + b.h - epsilon && a.y + a.h > b.y + epsilon) {
        ids.add(widget.id)
        ids.add(other.id)
      }
    }
  }
  return [...ids]
}

export function selectionBounds(widgets) {
  if (!widgets.length) return null
  const left = Math.min(...widgets.map((widget) => widget.layout.x))
  const top = Math.min(...widgets.map((widget) => widget.layout.y))
  return { x: left, y: top,
    w: Math.max(...widgets.map((widget) => widget.layout.x + widget.layout.w)) - left,
    h: Math.max(...widgets.map((widget) => widget.layout.y + widget.layout.h)) - top }
}

export function canvasPoint(event, element, scale) {
  const pointer = event.touches?.[0] || event.changedTouches?.[0] || event
  const rect = element?.getBoundingClientRect()
  if (!rect || !Number.isFinite(pointer.clientX) || !Number.isFinite(pointer.clientY)) return null
  return { x: (pointer.clientX - rect.left) / scale, y: (pointer.clientY - rect.top) / scale }
}

const clamp = (value, min, max) => Math.min(Math.max(value, min), Math.max(min, max))
const marks = (start, size) => [start, start + size / 2, start + size]

function targets(widgets, canvas, axis) {
  const horizontal = axis === "x"
  return [0, (horizontal ? canvas.width : canvas.height) / 2, horizontal ? canvas.width : canvas.height,
    ...widgets.flatMap(({ layout }) => marks(horizontal ? layout.x : layout.y, horizontal ? layout.w : layout.h))]
}

function nearest(moving, fixed, threshold) {
  let best = null
  for (const position of fixed) for (const point of moving) {
    const offset = position - point
    if (Math.abs(offset) <= threshold && (!best || Math.abs(offset) < Math.abs(best.offset))) best = { offset, position }
  }
  return best
}

function alignedGuides(rect, others, canvas, threshold = .01) {
  return ["x", "y"].flatMap((axis) => {
    const match = nearest(marks(rect[axis], axis === "x" ? rect.w : rect.h), targets(others, canvas, axis), threshold)
    return match ? [{ axis, position: match.position }] : []
  })
}

export function moveSelection(selected, others, canvas, delta, options = {}) {
  const bounds = selectionBounds(selected)
  if (!bounds) return { layouts: {}, guides: [] }
  const snap = options.snap ?? false
  const threshold = options.threshold ?? 6
  let dx = delta.x
  let dy = delta.y
  if (snap) {
    const x = nearest(marks(bounds.x + dx, bounds.w), targets(others, canvas, "x"), threshold)
    const y = nearest(marks(bounds.y + dy, bounds.h), targets(others, canvas, "y"), threshold)
    dx = x ? dx + x.offset : Math.round((bounds.x + dx) / CANVAS_GRID) * CANVAS_GRID - bounds.x
    dy = y ? dy + y.offset : Math.round((bounds.y + dy) / CANVAS_GRID) * CANVAS_GRID - bounds.y
  }
  // Clamp the group delta, never each widget independently: spacing is invariant.
  dx = bounds.w <= canvas.width ? clamp(dx, -bounds.x, canvas.width - bounds.x - bounds.w) : 0
  dy = bounds.h <= canvas.height ? clamp(dy, -bounds.y, canvas.height - bounds.y - bounds.h) : 0
  const moved = { ...bounds, x: bounds.x + dx, y: bounds.y + dy }
  return {
    layouts: Object.fromEntries(selected.map((widget) => [widget.id, { ...widget.layout, x: widget.layout.x + dx, y: widget.layout.y + dy }])),
    guides: snap ? alignedGuides(moved, others, canvas) : [],
  }
}

export function resizeSelection(widget, others, canvas, direction, delta, options = {}) {
  const original = widget.layout
  const edge = direction.toLowerCase()
  const left = edge.includes("left")
  const right = edge.includes("right")
  const top = edge.includes("top")
  const bottom = edge.includes("bottom")
  let x1 = original.x + (left ? delta.x : 0)
  let x2 = original.x + original.w + (right ? delta.x : 0)
  let y1 = original.y + (top ? delta.y : 0)
  let y2 = original.y + original.h + (bottom ? delta.y : 0)
  const snap = (value, axis) => {
    if (!options.snap) return value
    const target = nearest([value], targets(others, canvas, axis), options.threshold ?? 6)
    return target ? target.position : Math.round(value / CANVAS_GRID) * CANVAS_GRID
  }
  if (left) x1 = clamp(snap(x1, "x"), 0, x2 - Math.min(80, original.w))
  if (right) x2 = clamp(snap(x2, "x"), x1 + Math.min(80, canvas.width - x1), canvas.width)
  if (top) y1 = clamp(snap(y1, "y"), 0, y2 - Math.min(50, original.h))
  if (bottom) y2 = clamp(snap(y2, "y"), y1 + Math.min(50, canvas.height - y1), canvas.height)
  const layout = { x: x1, y: y1, w: x2 - x1, h: y2 - y1 }
  const guides = options.snap ? [
    ...(left || right ? alignedGuides({ x: left ? x1 : x2, y: y1, w: 0, h: 0 }, others, canvas).filter((guide) => guide.axis === "x") : []),
    ...(top || bottom ? alignedGuides({ x: x1, y: top ? y1 : y2, w: 0, h: 0 }, others, canvas).filter((guide) => guide.axis === "y") : []),
  ] : []
  return { layouts: { [widget.id]: layout }, guides }
}

export function marqueeSelection(widgets, start, end) {
  const rect = { x: Math.min(start.x, end.x), y: Math.min(start.y, end.y), w: Math.abs(start.x - end.x), h: Math.abs(start.y - end.y) }
  return { rect, ids: widgets.filter(({ layout }) => layout.x < rect.x + rect.w && layout.x + layout.w > rect.x
    && layout.y < rect.y + rect.h && layout.y + layout.h > rect.y).map((widget) => widget.id) }
}

export function cloneSelection(selected, canvas, id, offset = 24) {
  const bounds = selectionBounds(selected)
  if (!bounds || bounds.w > canvas.width || bounds.h > canvas.height) return null
  let moved = moveSelection(selected, [], canvas, { x: offset, y: offset })
  if (selected.every((widget) => moved.layouts[widget.id].x === widget.layout.x && moved.layouts[widget.id].y === widget.layout.y)) {
    moved = moveSelection(selected, [], canvas, { x: -offset, y: -offset })
  }
  return selected.map((widget) => ({ ...structuredClone(widget), id: id(), layout: moved.layouts[widget.id] }))
}

export function placeWithoutOverlap(widgets, others, canvas) {
  const bounds = selectionBounds(widgets)
  if (!bounds || bounds.w > canvas.width || bounds.h > canvas.height) return null
  if (!overlappingWidgetIds(widgets, others).length) return widgets
  const positions = (axis, size, maximum) => [...new Set([0, bounds[axis], ...others.flatMap(({ layout }) => [layout[axis] + layout[size] + CANVAS_GRID, layout[axis] - bounds[size] - CANVAS_GRID])])]
    .filter((point) => point >= 0 && point + bounds[size] <= maximum)
    .sort((a, b) => Math.abs(a - bounds[axis]) - Math.abs(b - bounds[axis])).slice(0, 64)
  const horizontal = positions("x", "w", canvas.width)
  const candidates = positions("y", "h", canvas.height).flatMap((y) => horizontal.map((x) => ({ x, y })))
    .sort((a, b) => Math.hypot(a.x - bounds.x, a.y - bounds.y) - Math.hypot(b.x - bounds.x, b.y - bounds.y))
  for (const candidate of candidates.slice(0, 2048)) {
    const moved = moveSelection(widgets, [], canvas, { x: candidate.x - bounds.x, y: candidate.y - bounds.y })
    if (!overlappingWidgetIds(widgets, others, moved.layouts).length) return widgets.map((widget) => ({ ...widget, layout: moved.layouts[widget.id] }))
  }
  return null
}
